import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const VECTOR_ELEMENTS = new Set(['svg', 'g', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon']);
const VECTOR_ATTRIBUTES = new Set([
  'viewBox', 'd', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
  'width', 'height', 'points', 'fill', 'fill-rule', 'fill-opacity', 'stroke',
  'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray',
  'stroke-dashoffset', 'stroke-opacity', 'opacity', 'transform', 'vector-effect',
  'preserveAspectRatio', 'clip-rule',
]);

@Component({
  selector: 'app-dashboard-icon',
  standalone: true,
  template: '@if (markup) { <span class="custom-icon" [innerHTML]="markup"></span> } @else { <ng-content></ng-content> }',
  styles: [':host { display: inline-flex; align-items: center; justify-content: center; width: 1.5rem; height: 1.5rem; flex-shrink: 0; vertical-align: middle; } .custom-icon { display: contents; }'],
  host: { 'aria-hidden': 'true' },
})
export class DashboardIcon {
  markup: SafeHtml | null = null;

  constructor(private readonly sanitizer: DomSanitizer) {}

  @Input() set svg(value: string | null | undefined) {
    this.markup = null;
    if (typeof value !== 'string' || !value.trim() || value.length > 32768 || typeof DOMParser === 'undefined') return;
    const parsed = new DOMParser().parseFromString(value, 'image/svg+xml');
    const root = parsed.documentElement;
    if (root.localName !== 'svg' || (root.namespaceURI && root.namespaceURI !== SVG_NAMESPACE) || parsed.doctype || parsed.querySelector('parsererror')) return;
    const output = document.implementation.createDocument(SVG_NAMESPACE, 'svg');
    let shapes = 0;
    const copy = (source: Element, target: Element): void => {
      for (const attribute of Array.from(source.attributes)) {
        if (!VECTOR_ATTRIBUTES.has(attribute.name) || /url\s*\(|javascript\s*:|data\s*:/i.test(attribute.value)) continue;
        target.setAttribute(attribute.name, attribute.value);
      }
      for (const child of Array.from(source.children)) {
        if ((child.namespaceURI && child.namespaceURI !== SVG_NAMESPACE) || !VECTOR_ELEMENTS.has(child.localName) || child.localName === 'svg') continue;
        const vector = output.createElementNS(SVG_NAMESPACE, child.localName);
        copy(child, vector);
        target.appendChild(vector);
        if (child.localName !== 'g') shapes++;
      }
    };
    copy(root, output.documentElement);
    if (!shapes) return;
    const svg = output.documentElement;
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    if (!svg.hasAttribute('viewBox')) svg.setAttribute('viewBox', '0 0 24 24');
    // Trust only the newly constructed vector tree, never the supplied markup.
    this.markup = this.sanitizer.bypassSecurityTrustHtml(new XMLSerializer().serializeToString(svg));
  }
}
